import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiRequestError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { Field, InlineError, Spinner, TextInput } from '../components/ui.js';
import { DemoBanner } from '../components/DemoBanner.js';

export function LoginPage() {
  const { t, lang, setLang } = useI18n();
  const { user, isLoading, signIn } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && user) {
    return <Navigate to={user.mustChangePassword ? '/password' : '/'} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const signedIn = await signIn(username.trim(), password);
      navigate(signedIn.mustChangePassword ? '/password' : '/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : t('login.failed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_minmax(26rem,32rem)]">
      {/* Identity panel. Plain navy, no stock imagery -- this is a record room
          tool, and a photograph would be decoration pretending to be design. */}
      <div className="relative hidden flex-col justify-between bg-navy-800 p-10 text-white lg:flex">
        <div className="flex items-center gap-3">
          <svg width="34" height="34" viewBox="0 0 32 32" aria-hidden="true">
            <rect width="32" height="32" rx="7" fill="#fff" fillOpacity="0.1" />
            <path
              d="M9 8.5h9.5L23 13v10.5a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1Z"
              fill="none"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path d="M18 8.5V13h5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M12.5 17.5h7M12.5 20.5h4.5" stroke="#F0B429" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <div className="leading-tight">
            <p className="font-semibold">{t('app.name')}</p>
            <p className="text-[0.8125rem] text-white/60">{t('app.org')}</p>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="text-[1.75rem] font-semibold leading-snug tracking-tight">
            {lang === 'hi'
              ? 'हर पट्टा, हर फाइल — कुछ ही सेकंड में।'
              : 'Every Patta, every file — found in seconds.'}
          </h1>
          <p className="mt-3 text-white/70">
            {lang === 'hi'
              ? 'कक्ष, रैक, कॉलम और बैग तक सटीक स्थान। किसने ली, कब ली और कब लौटाई — पूरा विवरण सुरक्षित।'
              : 'Exact shelf location down to room, rack, column and bag. A complete, permanent record of who took each file, when, and when it came back.'}
          </p>
        </div>

        <p className="text-[0.8125rem] text-white/45">{t('app.tagline')}</p>
      </div>

      {/* Form */}
      <div className="flex flex-col justify-center bg-surface px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          {/* Before anyone signs in, so nobody mistakes the demonstration for
              the department's live record room. */}
          <div className="-mt-6 mb-6 overflow-hidden rounded-[5px]">
            <DemoBanner />
          </div>
          <div className="mb-8 flex items-center justify-between lg:hidden">
            <p className="font-semibold">{t('app.name')}</p>
          </div>

          <h2 className="text-xl font-semibold">{t('login.title')}</h2>
          <p className="mt-1.5 text-ink-500">{t('login.subtitle')}</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" noValidate>
            {error && <InlineError message={error} />}

            <Field label={t('login.username')} htmlFor="username" required>
              <TextInput
                id="username"
                name="username"
                autoComplete="username"
                autoFocus
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </Field>

            <Field label={t('login.password')} htmlFor="password" required>
              <TextInput
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting || username.length === 0 || password.length === 0}
            >
              {submitting && <Spinner />}
              {t('action.signIn')}
            </button>

            {/* The server deliberately will not say "this account is locked" --
                that would confirm the username exists and hand an attacker a
                correct/incorrect oracle. So the policy is stated here instead,
                where it is public information and helps the person signing in. */}
            <p className="pt-1 text-[0.8125rem] text-ink-500">{t('login.lockoutNotice')}</p>
          </form>

          <div className="mt-8 flex items-center justify-center gap-1 text-[0.8125rem]">
            <span className="text-ink-500">{t('common.language')}:</span>
            <button
              type="button"
              className="btn-ghost px-2 py-1"
              onClick={() => setLang(lang === 'en' ? 'hi' : 'en')}
            >
              {lang === 'en' ? 'हिंदी में देखें' : 'View in English'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
